/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { CosmosButtonFragment, FeaturePanelFragment } from "@cosmos/hygraph-schema";
import { ReactNode } from "react";

import {
	TitleDescStack,
	LinkList,
	CosmosAsset,
	CosmosTag,
	ButtonList,
	CosmosButtonProps,
	CosmosText,
	CosmosCardLink,
} from "@components/elements";
import { css, cx } from "@panda/css";
import { Flex, Stack } from "@panda/jsx";
import { flex } from "@panda/patterns";

const linkSection = (links: FeaturePanelFragment["links"], linksAsTags?: boolean | null): ReactNode => {
	if (links && links.length > 0) {
		return linksAsTags ? (
			<div
				className={css({
					display: "flex",
					flexWrap: "wrap",
					gap: "eds.dimension.12",
					minHeight: "eds.dimension.32",
					zIndex: 10,
				})}
			>
				{links.map((link) => (
					<CosmosTag
						className={css({
							"& svg": {
								height: "eds.dimension.12!", // override small EDS icon size
								width: "eds.dimension.12!",
							},
						})}
						key={link.label}
						label={link.label}
						href={link.href || ""}
						icon={link.href ? "ChevronRight" : undefined}
						iconPosition="end"
						size="uiSM"
					/>
				))}
			</div>
		) : (
			<LinkList links={links} />
		);
	} else {
		return undefined;
	}
};

export interface FeaturePanelProps extends Omit<FeaturePanelFragment, "buttons"> {
	className?: string;
	buttons?: CosmosButtonProps[] | CosmosButtonFragment[];
	disableCardBg?: boolean | null;
	verticallyCenter?: boolean | null;
}

export function FeaturePanel({
	eyebrowBadges,
	eyebrow,
	title,
	gradient,
	desc,
	links,
	buttons,
	asset,
	flip,
	className = "",
	linksAsTags = true,
	disableCardBg = false,
	verticallyCenter = false,
}: FeaturePanelProps): React.JSX.Element {
	const cosmosButtons = buttons?.map((button): CosmosButtonProps => {
		return {
			...button,
			size: "md",
			icon: "ArrowRightCircle",
			iconPosition: "end",
			variant: button.variant || "secondary",
			analytics: {
				eventCategory: "button",
				interactionType: "click",
				eventAction: "navigation",
				eventLabel: button.name || button.label || "",
				eventValue: {
					component: "FeatureCard",
					section: "section",
				},
			},
		};
	});
	const href = cosmosButtons && cosmosButtons[0] && cosmosButtons[0].href ? cosmosButtons[0].href : undefined;

	return (
		<div
			className={cx(
				"feature-panel",
				className,
				flex({
					flexDir: "row",
					width: "100%",
					padding: disableCardBg ? "0" : "eds.dimension.16",
					gap: disableCardBg ? "eds.dimension.48" : "eds.dimension.16",
					lgDown: { flexDir: "column", gap: disableCardBg ? "eds.dimension.32" : "eds.dimension.16" },
					smDown: { gap: disableCardBg ? "eds.dimension.24" : "eds.dimension.16" },
					backgroundColor: disableCardBg ? "" : "eds.palette.transparent.light.005",
					backdropFilter: disableCardBg ? "" : "var(--effect-material-blur, blur(50px))",
					borderRadius: disableCardBg ? "" : "eds.borderRadius.24",
					border: disableCardBg
						? ""
						: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.005)",
				}),
				css(
					!disableCardBg && {
						"&:has(.cosmos-card__link[href]:hover), &:has(.cosmos-card__link[href]:focus-visible)": {
							"& .feature-panel-asset-wrapper": {
								transform: "scale(1.05)",
							},
						},
						"&:has(.cosmos-card__link[href]:hover)": {
							backgroundColor: "eds.palette.transparent.light.010",
						},
						transition: "background token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad)",
						"& .link-list": {
							zIndex: 10,
						},
						"& .cosmos-video-embed": {
							borderRadius: disableCardBg
								? { base: "eds.borderRadius.24", smDown: "eds.borderRadius.16" }
								: "eds.borderRadius.8",
						},
					},
				),
			)}
		>
			<div
				className={cx(
					"content",
					css({
						flexBasis: "1/2",
						flexGrow: "1",
						minWidth: "0",
						width: "100%",
						alignSelf: verticallyCenter ? "center" : "",
						padding: disableCardBg ? "0" : "eds.dimension.32",
						lgDown: { padding: disableCardBg ? "0" : "eds.dimension.16" },
						smDown: { padding: "0" },
					}),
				)}
			>
				<Stack
					className={cx(
						"title-desc-links-column",
						css({
							"& .link-list": {
								"& .cosmos-text": {
									fontSize: "eds.font.14",
								},
								"& svg": {
									height: "eds.dimension.20!",
									width: "eds.dimension.20!",
								},
								fontSize: "eds.font.14",
							},
							lg: { gap: "eds.dimension.24" },
						}),
					)}
					direction="column"
					gap="eds.dimension.16"
				>
					{eyebrowBadges?.length ? (
						<Flex gap="eds.dimension.12" flexWrap="wrap" alignItems="center">
							{eyebrowBadges.map((badge) => (
								<CosmosTag
									key={`feature-panel-eyebrow-badge-${badge.label}`}
									label={badge.label}
									href={badge.href || ""}
									size="uiSM"
								/>
							))}
						</Flex>
					) : null}
					{eyebrow ? <CosmosText variant="eyebrowMD" text={eyebrow} color="eds.text.secondary" /> : null}
					<TitleDescStack
						gradient={gradient}
						title={{ html: title?.html, text: title?.text, variant: "headingLG" }}
						description={{ html: desc, variant: "paragraphMD" }}
						stack={{ gap: "eds.dimension.16" }}
						className={css({
							smDown: {
								gap: "eds.dimension.8",
								"& .cosmos-text.heading": {
									textStyle: "headingMD",
								},
								"& .cosmos-rich-text.paragraph": {
									textStyle: "paragraphSM",
								},
							},
						})}
					/>
					{linkSection(links, linksAsTags)}
					{cosmosButtons ? <ButtonList buttons={cosmosButtons} className={css({ zIndex: 10 })} /> : null}
				</Stack>
			</div>
			<Flex
				alignItems="stretch"
				justifyContent="center"
				objectFit="contain"
				minWidth="0"
				className={cx(
					flip === true ? "image flip" : "image",
					css({
						order: -1,
						overflow: disableCardBg ? "visible" : "hidden",
						lg: { order: flip === true ? 1 : -1 },
						sm: { flexBasis: disableCardBg ? "3/4" : "1/2" },
						borderRadius: disableCardBg
							? { base: "eds.borderRadius.16", smDown: "eds.borderRadius.8" }
							: "eds.borderRadius.8",
					}),
				)}
			>
				<div className={css({ position: "relative", width: "full", height: "full" })}>
					{/* This additional Flex is necessary in order to always keep the image aspect ratio, preventing incorrect scaling. */}
					<Flex
						alignItems="stretch"
						justifyContent="center"
						w="full"
						h="full"
						className={cx(
							"feature-panel-asset-wrapper",
							css({
								aspectRatio: "16 / 9",
								"& .cosmos-attribution": {
									zIndex: 2,
								},
								transition: "transform token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad)",
							}),
						)}
					>
						<CosmosAsset
							asset={{
								...(asset || {}),
								id: asset?.id || "",
								url: asset?.url || "",
								width: 1113,
								height: 627,
							}}
							className={css({
								objectFit: "cover",
								h: "full", // Need to set the height to full in order for Safari to scale correctly
								w: "full",
								position: "relative",
								zIndex: 2,
								borderRadius: disableCardBg
									? { base: "eds.borderRadius.24", smDown: "eds.borderRadius.16" }
									: "eds.borderRadius.8",
							})}
						/>
					</Flex>
				</div>
			</Flex>
			{!disableCardBg && (
				<CosmosCardLink
					href={href || ""}
					className={css({ zIndex: 2, margin: { base: "-eds.dimension.16", smDown: "-eds.dimension.8" } })}
				/>
			)}
		</div>
	);
}
